import React, { useState, useEffect } from 'react'
import { concatStyles, humanize } from 'utils'

import { Input } from '../core/input'
import { Button } from '../core/button'
import * as Card from '../Card'
import Filter from './Filter'

export interface FiltersCardProps {
  facets: any
  attributeTransforms?: { [key: string]: string }
  appliedFacets: string[]
  activeFacets?: string[] // Currently applied facets (what affects search results)
  initialQueryString?: string
  hideQueryString?: boolean
  onQuery?: (value: string) => void
  onToggle: (value: string, action: 'add' | 'remove') => void
  onApplyFilters?: (filters: {
    appliedFacets: string[]
    searchQuery: string
  }) => void
  onResetFilters?: () => void
  onEnterKeySubmit?: () => void
  maxFilterBeforeReveal?: number
  maxVisibleFilters?: number
  className?: string
  style?: React.CSSProperties
  hideFilterActions?: boolean
  hideActiveFiltersList?: boolean
  loading?: boolean
  disableApplyButton?: boolean
  applyButtonDisabledMessage?: string
  excludedAttributes?: string[]
}

const SHARED_STYLES = 'flex m-0 box-border flex-shrink-0'

const CONTAINER_STYLES = `${SHARED_STYLES} flex-col flex justify-between w-full`

const INPUT_CONTAINER_STYLES = `${SHARED_STYLES} w-full mb-4`

export const FiltersCard: React.FC<FiltersCardProps> = ({
  appliedFacets: externalAppliedFacets,
  activeFacets: externalActiveFacets,
  facets,
  attributeTransforms,
  onToggle: externalHandleToggle,
  initialQueryString,
  maxFilterBeforeReveal,
  onQuery: externalHandleQuery,
  onApplyFilters,
  onResetFilters,
  onEnterKeySubmit,
  hideQueryString,
  hideFilterActions = false,
  hideActiveFiltersList = false,
  className,
  style,
  maxVisibleFilters = 5,
  loading = false,
  disableApplyButton = false,
  applyButtonDisabledMessage,
  excludedAttributes = [],
}) => {
  // Internal state to track filters and query
  const [internalAppliedFacets, setInternalAppliedFacets] = useState<string[]>(
    externalAppliedFacets || []
  )
  const [searchInputText, setSearchInputText] = useState(
    initialQueryString ?? ''
  )
  const [filtersApplied, setFiltersApplied] = useState(false)
  const actualExternalFacets = externalActiveFacets?.filter((f) => f !== '')
  // Sync internal state with external props
  useEffect(() => {
    setInternalAppliedFacets(externalAppliedFacets || [])
  }, [externalAppliedFacets])

  useEffect(() => {
    setSearchInputText(initialQueryString ?? '')
  }, [initialQueryString])

  // Handle search query updates
  const updateSearchQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value
    setSearchInputText(value)
    if (externalHandleQuery) {
      externalHandleQuery(value)
    }
  }

  // Handle Enter key in search input
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' &&
      onEnterKeySubmit &&
      !disableApplyButton &&
      !loading
    ) {
      e.preventDefault()
      onEnterKeySubmit()
    }
  }

  // Handle checkbox state changes
  const handleCheckBox = (value: string) => (checked: boolean) => {
    const action = checked ? 'add' : 'remove'

    // Update internal state
    if (action === 'add') {
      setInternalAppliedFacets((prev) => [...prev, value])
    } else {
      setInternalAppliedFacets((prev) => prev.filter((f) => f !== value))
    }

    // Call external handler
    externalHandleToggle(value, action)
  }

  // Handle filter visibility toggle
  const [showAllFilters, setShowAllFilters] = useState(false)
  const handleToggleFilters = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowAllFilters(!showAllFilters)
  }

  // Handle apply filters
  const handleApplyFilters = () => {
    setFiltersApplied(true)

    if (onApplyFilters) {
      onApplyFilters({
        appliedFacets: internalAppliedFacets,
        searchQuery: searchInputText,
      })
    }

    // Reset the applying state after a delay
    setTimeout(() => {
      setFiltersApplied(false)
    }, 1000)
  }

  // Handle reset filters
  const handleResetFilters = () => {
    setInternalAppliedFacets([])
    setSearchInputText('')

    if (onResetFilters) {
      onResetFilters()
    }

    // Also call external handlers with empty values
    if (externalHandleQuery) {
      externalHandleQuery('')
    }
  }

  const maxFilters = maxFilterBeforeReveal ?? 3

  // Format facet display values for active filters list
  const getFormattedFilter = (filter?: string): string => {
    if (!filter) return ''
    const [attribute, value] = filter?.split(':')
    const displayAttribute =
      attributeTransforms?.[attribute] || humanize(attribute)
    return `${displayAttribute}: ${humanize(value)}`
  }

  return (
    <Card.View
      className={concatStyles(['p-3 overflow-y-auto', className])}
      style={{ maxHeight: '100vh', ...style }}
    >
      <div className={CONTAINER_STYLES}>
        {!hideQueryString ? (
          <div className={INPUT_CONTAINER_STYLES}>
            <Input
              onChange={updateSearchQuery}
              onKeyDown={handleSearchKeyDown}
              name="Search Query"
              placeholder="Keywords, name, etc."
              value={searchInputText}
            />
          </div>
        ) : null}

        {Object.keys(facets)
          .filter((attribute) => !excludedAttributes.includes(attribute))
          .filter((_, i) => showAllFilters || i < maxFilters)
          .map((attribute) => (
            <Filter
              key={attribute}
              attribute={attribute}
              onChange={handleCheckBox}
              facet={facets[attribute]}
              appliedFacets={internalAppliedFacets}
              maxVisibleFilters={maxVisibleFilters}
              transformLabel={attributeTransforms?.[attribute]}
            />
          ))}

        {Object.keys(facets).filter(
          (attr) => !excludedAttributes.includes(attr)
        ).length > maxFilters ? (
          <p className="w-full text-center flex-shrink-0">
            <Button
              className="cursor-pointer text-left text-actionable"
              onClick={handleToggleFilters}
              size="sm"
              variant="ghost"
            >
              {showAllFilters ? 'Hide' : 'Show All'} Filters
            </Button>
          </p>
        ) : null}

        {/* Filter actions */}
        {!hideFilterActions && (
          <div className="flex flex-col mt-4">
            <div className="flex space-x-2">
              <Button
                onClick={handleApplyFilters}
                disabled={filtersApplied || loading || disableApplyButton}
                className="flex-1"
                title={
                  disableApplyButton && applyButtonDisabledMessage
                    ? applyButtonDisabledMessage
                    : undefined
                }
              >
                {loading
                  ? 'Loading...'
                  : filtersApplied
                  ? 'Applying...'
                  : `Apply Filters (${
                      internalAppliedFacets.filter((f) => f.length > 0).length
                    })`}
              </Button>
              <Button
                onClick={handleResetFilters}
                variant="outline"
                disabled={
                  internalAppliedFacets.length === 0 && searchInputText === ''
                }
              >
                Reset
              </Button>
            </div>
            {disableApplyButton && applyButtonDisabledMessage && (
              <p className="text-xs text-gray-500 mt-2 italic">
                {applyButtonDisabledMessage}
              </p>
            )}
          </div>
        )}

        {/* Active filters list - showing only applied filters */}
        {!hideActiveFiltersList && (actualExternalFacets?.length ?? 0) > 0 && (
          <div className="mt-4 p-3 border rounded-md bg-gray-50">
            <h3 className="text-sm font-bold mb-2">Active Filters:</h3>
            <ul className="space-y-1">
              {actualExternalFacets?.map((filter) => (
                <li key={filter} className="text-xs flex items-center">
                  <span className="mr-1">•</span>
                  <span className="flex-grow">
                    {getFormattedFilter(filter)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-1 text-xs h-5 px-1 text-gray-500 hover:text-red-500"
                    onClick={() => handleCheckBox(filter)(false)}
                  >
                    ✕
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card.View>
  )
}
